import type { User } from '@/store/authSessionSlice'

/**
 * The subset of an organization member this module needs to resolve someone
 * who is not the current user. Structural on purpose: both `OrganizationUser`
 * (from `getOrganizationUsers`) and a `searchOrganizationUsers` row satisfy it,
 * so callers can pass whichever they already hold.
 *
 * `picture` here is the IdP claim — the Google/Gmail photo the OrgSyncInterceptor
 * stored for whichever identity the person signed in with. An avatar someone
 * UPLOADED lives in `avatar_path` and is served only by `GET
 * /v1/organization/me/avatar`, so it is reachable for yourself and nobody else.
 * For the current user that distinction is already handled upstream:
 * authSessionSlice resolves `user.picture` as `uploadedAvatarUrl ?? keycloakUser.picture`.
 */
export interface UserDirectoryEntry {
  user_id: string | null
  name: string | null
  picture: string | null
}

const findInDirectory = (
  userId: string,
  directory: readonly UserDirectoryEntry[]
): UserDirectoryEntry | undefined =>
  // Array.isArray, because this is reached from components rendering inside a
  // canvas shape where the store may not be hydrated (or is mocked narrowly);
  // a throw here would take the board down rather than degrade to an initial.
  userId && Array.isArray(directory)
    ? directory.find((entry) => entry.user_id === userId)
    : undefined

export interface UserInfo {
  id: string
  name: string
  email: string
  avatar?: string
}

/**
 * Gets user information for display purposes
 * @param userId - The user ID to get info for
 * @param currentUser - The current authenticated user
 * @param emailSuffix - Email suffix to append for fallback emails
 * @returns UserInfo object with display information
 */
export const getUserInfo = (
  userId: string,
  currentUser: User | null,
  emailSuffix: string = '@intuigence.ai'
): UserInfo => {
  // If it's the current user, use their actual info
  if (currentUser && currentUser.userId === userId) {
    return {
      id: userId,
      name: currentUser.name || userId || 'Unknown User', // Fallback for null names
      email: currentUser.email || `${userId}${emailSuffix}`,
      avatar: currentUser.picture,
    }
  }

  // Fallback for other users - you could extend this to fetch from API
  return {
    id: userId,
    name: userId || 'Unknown User', // Use userId as name for now
    email: `${userId}${emailSuffix}`,
  }
}

/**
 * Gets the display name for a user
 * @param userId - The user ID
 * @param currentUser - The current authenticated user
 * @returns The display name for the user
 */
export const getUserDisplayName = (
  userId: string,
  currentUser: User | null,
  directory: readonly UserDirectoryEntry[] = []
): string => {
  if (currentUser && currentUser.userId === userId) {
    return currentUser.name || userId || 'Unknown User'
  }
  const known = findInDirectory(userId, directory)
  if (known?.name) return known.name
  // Unchanged fallback: callers that pass no directory keep the previous
  // behaviour rather than silently turning every author into 'Unknown User'.
  return userId || 'Unknown User'
}

/**
 * Gets the display email for a user
 * @param userId - The user ID
 * @param currentUser - The current authenticated user
 * @param emailSuffix - Email suffix to append for fallback emails
 * @returns The display email for the user
 */
export const getUserDisplayEmail = (
  userId: string,
  currentUser: User | null,
  emailSuffix: string = '@intuigence.ai'
): string => {
  if (currentUser && currentUser.userId === userId) {
    return currentUser.email
  }
  return `${userId}${emailSuffix}`
}

/**
 * Gets the user's avatar or initials
 * @param userId - The user ID
 * @param currentUser - The current authenticated user
 * @returns The avatar URL or null if not available
 */
export const getUserAvatar = (
  userId: string,
  currentUser: User | null,
  directory: readonly UserDirectoryEntry[] = []
): string | null => {
  if (currentUser && currentUser.userId === userId) {
    // Upload first (authSessionSlice already merged it into `picture`), then
    // the token's `picture` claim — and then the DIRECTORY, which is the part
    // that was missing. The claim is only present if Keycloak's token carries
    // it; when it does not, `organization_members.picture` still holds the
    // Google photo (OrgSyncInterceptor writes the claim there on every
    // authenticated request), and `GET /v1/organization/users` returns it for
    // every member including yourself. Without this fall-through we showed an
    // initial while holding the photo.
    return currentUser.picture || findInDirectory(userId, directory)?.picture || null
  }
  return findInDirectory(userId, directory)?.picture || null
}

/**
 * Gets the user's initials for avatar fallback
 * @param userId - The user ID
 * @param currentUser - The current authenticated user
 * @returns The user's initials (first letter of name)
 */
export const getUserInitials = (
  userId: string,
  currentUser: User | null
): string => {
  const displayName = getUserDisplayName(userId, currentUser)
  // Safely get first character with fallback
  return displayName && displayName.length > 0
    ? displayName.charAt(0).toUpperCase()
    : 'U' // Fallback to 'U' for Unknown
}

/**
 * The letter to paint on an avatar — but ONLY when we actually know the
 * person's name. Returns `null` otherwise, so the caller renders a neutral
 * glyph instead of a character with no meaning.
 *
 * This is the FS-581 "random character such as `6`" bug. `getUserDisplayName`
 * falls back to the raw user id for anyone who is not the current user, so
 * `charAt(0)` of a UUID rendered whatever the id happened to start with — a
 * digit, a stray letter, different per person and informative to nobody.
 * `getUserInitials` still does that (BoardTable depends on it); this is the
 * variant avatar surfaces should use.
 */
export const getUserAvatarInitial = (
  userId: string,
  currentUser: User | null,
  directory: readonly UserDirectoryEntry[] = []
): string | null => {
  const name =
    currentUser && currentUser.userId === userId
      ? currentUser.name || findInDirectory(userId, directory)?.name
      : findInDirectory(userId, directory)?.name
  const trimmed = name?.trim()
  // A name that IS the id is not a name — the directory can echo the id back.
  if (!trimmed || trimmed === userId) return null
  return trimmed.charAt(0).toUpperCase()
}

/**
 * The one user-avatar background used across the app (FS-581) — the same
 * cyan→sky gradient as the profile bubble in the left rail. Comment avatars
 * previously hashed the user id into per-user random colors, which made the
 * same user render different colors in different surfaces.
 */
export const USER_AVATAR_GRADIENT_CLASS =
  'bg-gradient-to-br from-cyan-400 to-sky-500'
